import { useEffect, useState, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import { applyTheme, GRAPHIKPRO_TOKENS } from "@/context/ThemeProvider";
import useDocumentTitle from "@/hooks/useDocumentTitle";
import api, { formatApiError } from "@/lib/api";
import ProvisionTenantDialog from "@/components/ProvisionTenantDialog";
import RiskConfigDialog from "@/components/RiskConfigDialog";
import BillingPanel from "@/components/BillingPanel";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Command, LogOut, Building2, Bike, Users, Activity, Lock, IndianRupee } from "lucide-react";
import { ThemeToggle } from "@/components/ThemeToggle";
import { toast } from "sonner";

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="rounded-tenant-card border border-border bg-tenant-surface p-5 shadow-tenant-soft">
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Icon className="h-4 w-4" /> {label}
      </div>
      <p className="mt-2 text-3xl font-semibold tracking-tight">{value}</p>
    </div>
  );
}

function swatch(tokens, key) {
  return `hsl(${(tokens || {})[key] || "0 0% 80%"})`;
}

export default function AdminConsole() {
  const { user, logout } = useAuth();
  const [overview, setOverview] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [meta, setMeta] = useState({ plans: [], features: [] });
  const [tab, setTab] = useState("operators");

  useEffect(() => { applyTheme(GRAPHIKPRO_TOKENS); }, []);
  useDocumentTitle(tab === "billing" ? "Commercial & billing · Admin" : "Operators · Admin");

  const load = useCallback(async () => {
    const [o, t, m] = await Promise.all([
      api.get("/platform/overview"),
      api.get("/platform/tenants"),
      api.get("/platform/meta"),
    ]);
    setOverview(o.data); setTenants(t.data); setMeta(m.data);
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggle = async (tenantId, featureKey, enabled) => {
    try {
      const { data } = await api.patch(`/platform/tenants/${tenantId}/features`, {
        feature_key: featureKey, enabled,
      });
      setTenants((prev) => prev.map((t) => (t.id === tenantId ? data : t)));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not toggle feature");
    }
  };

  return (
    <div className="min-h-screen bg-tenant-bg">
      <header className="sticky top-0 z-10 border-b border-border bg-tenant-surface/80 backdrop-blur">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-3">
          <div className="flex items-center gap-2 font-semibold">
            <Command className="h-5 w-5" /> Graphikpro
            <span className="rounded-full bg-muted px-2 py-0.5 text-xs font-normal text-muted-foreground">
              Super-admin console
            </span>
          </div>
          <div className="flex items-center gap-3">
            <span className="hidden text-sm text-muted-foreground sm:inline">{user?.email}</span>
            <ThemeToggle />
            <Button data-testid="logout-btn" variant="ghost" size="sm" onClick={logout} className="gap-1.5">
              <LogOut className="h-4 w-4" /> Sign out
            </Button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl space-y-8 px-6 py-8">
        <div className="flex items-end justify-between">
          <div>
            <h1 className="text-3xl font-semibold tracking-tight">
              {tab === "operators" ? "Operators" : "Commercial & billing"}
            </h1>
            <p className="mt-1 text-sm text-muted-foreground">
              {tab === "operators"
                ? "Cross-tenant health and provisioning. Every operator is fully isolated."
                : "Subscriptions, plan changes, monthly platform invoices and account suspension."}
            </p>
          </div>
          {tab === "operators" && meta.plans.length > 0 && (
            <ProvisionTenantDialog plans={meta.plans} features={meta.features} onProvisioned={load} />
          )}
        </div>

        <div className="inline-flex rounded-tenant-card border border-border bg-tenant-surface p-1 shadow-tenant-soft">
          <button
            data-testid="admin-tab-operators"
            onClick={() => setTab("operators")}
            className={`inline-flex items-center gap-1.5 rounded-md px-4 py-1.5 text-sm font-medium transition-colors ${tab === "operators" ? "bg-tenant-primary text-tenant-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>
            <Building2 className="h-4 w-4" /> Operators
          </button>
          <button
            data-testid="admin-tab-billing"
            onClick={() => setTab("billing")}
            className={`inline-flex items-center gap-1.5 rounded-md px-4 py-1.5 text-sm font-medium transition-colors ${tab === "billing" ? "bg-tenant-primary text-tenant-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>
            <IndianRupee className="h-4 w-4" /> Commercial & billing
          </button>
        </div>

        {tab === "billing" ? (
          <div data-testid="billing-panel"><BillingPanel plans={meta.plans} /></div>
        ) : (
          <>
        {overview && (
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
            <Stat icon={Building2} label="Operators" value={overview.total_tenants} />
            <Stat icon={Activity} label="Active" value={overview.active_tenants} />
            <Stat icon={Bike} label="Vehicles (all tenants)" value={overview.total_vehicles} />
            <Stat icon={Users} label="Operator users" value={overview.total_operator_users} />
          </div>
        )}

        <div className="space-y-4">
          {tenants.map((t) => (
            <div key={t.id} data-testid={`tenant-card-${t.subdomain}`}
              className="rounded-tenant-card border border-border bg-tenant-surface p-5 shadow-tenant-soft">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className="flex h-11 w-11 items-center justify-center rounded-xl text-sm font-bold text-white"
                    style={{ background: swatch(t.branding?.tokens, "--tenant-primary") }}>
                    {t.name.slice(0, 2).toUpperCase()}
                  </div>
                  <div>
                    <p className="font-semibold">{t.name}</p>
                    <p className="text-sm text-muted-foreground">{t.subdomain}.fleetos.app · {t.plan_name}</p>
                  </div>
                </div>
                <div className="flex items-center gap-4 text-sm">
                  <span className="text-muted-foreground">
                    {t.usage.vehicles}/{t.usage.vehicle_limit} vehicles
                  </span>
                  <span className="text-muted-foreground">
                    {t.usage.users}/{t.usage.user_limit} users
                  </span>
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-status-available/15 px-2.5 py-1 text-xs font-medium text-status-available">
                    <span className="h-1.5 w-1.5 rounded-full bg-current" /> {t.status}
                  </span>
                </div>
              </div>

              <div className="mt-4 flex items-center gap-2 border-t border-border pt-4">
                <span className="text-xs font-medium text-muted-foreground">Theme</span>
                <span className="h-5 w-5 rounded-full border border-border" style={{ background: swatch(t.branding?.tokens, "--tenant-primary") }} />
                <span className="h-5 w-5 rounded-full border border-border" style={{ background: swatch(t.branding?.tokens, "--tenant-accent") }} />
                <div className="ml-auto"><RiskConfigDialog tenantId={t.id} tenantName={t.name} /></div>
              </div>

              <div className="mt-4">
                <p className="mb-2 text-xs font-medium text-muted-foreground">Feature entitlements</p>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
                  {t.entitlements.filter((e) => meta.features.find((f) => f.key === e.feature_key && !f.internal_only)).map((e) => {
                    const f = meta.features.find((x) => x.key === e.feature_key);
                    return (
                      <div key={e.feature_key}
                        className="flex items-center justify-between rounded-lg border border-border px-3 py-2">
                        <div className="flex items-center gap-1.5">
                          {!e.in_plan && <Lock className="h-3 w-3 text-muted-foreground" />}
                          <span className={`text-sm ${e.in_plan ? "" : "text-muted-foreground"}`}>{f?.label}</span>
                        </div>
                        <Switch data-testid={`feature-${t.subdomain}-${e.feature_key}`}
                          checked={e.enabled} disabled={!e.in_plan}
                          onCheckedChange={(v) => toggle(t.id, e.feature_key, v)} />
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          ))}
        </div>
        </>
        )}
      </main>
    </div>
  );
}
